import { Truck, Droplets, Package, CheckCircle, Home, UserCheck, Sofa, WashingMachine, BrushCleaning, Clock3 } from 'lucide-react';

// Фиксированный порядок этапов и их цвета
export const STAGES_ORDER = [
  'pickup',
  'washing',
  'washing_cleaning',
  'washing_laundry',
  'drying_packing',
  'approval',
  'delivery',
  'handover',
  'cleaning'
];

// Этапы, которые должны видеть и настраивать сотрудники в UI.
// "washing" остаётся внутренним макро-статусом заказа и не показывается как отдельный рабочий этап.
export const USER_STAGE_ORDER = STAGES_ORDER.filter((stageId) => stageId !== 'washing');

// Маппинг иконок
const STAGE_ICONS = {
  pickup: Truck,
  washing: Droplets,
  washing_laundry: WashingMachine,
  washing_cleaning: BrushCleaning,
  drying_packing: Package,
  approval: CheckCircle,
  delivery: Home,
  handover: UserCheck,
  cleaning: Sofa
};

export const STAGES_CONFIG = {
  pickup: { label: 'Доставка от клиента', icon: Truck, color: 'amber' },
  washing: { label: 'Стирка', icon: Droplets, color: 'cyan' },
  washing_laundry: { label: 'Прачечная', icon: WashingMachine, color: 'indigo' },
  washing_cleaning: { label: 'Чистка', icon: BrushCleaning, color: 'rose' },
  drying_packing: { label: 'Сушка и упаковка', icon: Package, color: 'purple' },
  approval: { label: 'Согласование', icon: CheckCircle, color: 'yellow' },
  delivery: { label: 'Доставка клиенту', icon: Home, color: 'teal' },
  handover: { label: 'Выдача', icon: UserCheck, color: 'lime' },
  cleaning: { label: 'Химчистка мебели', icon: Sofa, color: 'fuchsia' },
  deferred: { label: 'Отложенные', icon: Clock3, color: 'orange' }
};

// Стили для активной вкладки (чтобы Tailwind видел полные имена классов)
export const STAGE_TAB_STYLES = {
  amber: 'border-amber-500 text-amber-600 dark:text-amber-400',
  orange: 'border-orange-500 text-orange-600 dark:text-orange-400',
  cyan: 'border-cyan-500 text-cyan-600 dark:text-cyan-400',
  rose: 'border-rose-500 text-rose-600 dark:text-rose-400',
  violet: 'border-violet-500 text-violet-600 dark:text-violet-400',
  indigo: 'border-indigo-500 text-indigo-600 dark:text-indigo-400',
  purple: 'border-purple-500 text-purple-600 dark:text-purple-400',
  yellow: 'border-yellow-500 text-yellow-600 dark:text-yellow-400',
  teal: 'border-teal-500 text-teal-600 dark:text-teal-400',
  lime: 'border-lime-500 text-lime-600 dark:text-lime-400',
  fuchsia: 'border-fuchsia-500 text-fuchsia-600 dark:text-fuchsia-400',
  green: 'border-green-500 text-green-600 dark:text-green-400',
  emerald: 'border-emerald-500 text-emerald-600 dark:text-emerald-400',
  blue: 'border-blue-500 text-blue-600 dark:text-blue-400',
};

// Стили для фона иконки этапа
export const STAGE_BG_STYLES = {
  amber: 'from-amber-500 to-amber-600',
  orange: 'from-orange-500 to-orange-600',
  cyan: 'from-cyan-500 to-cyan-600',
  rose: 'from-rose-500 to-rose-600',
  violet: 'from-violet-500 to-violet-600',
  indigo: 'from-indigo-500 to-indigo-600',
  purple: 'from-purple-500 to-purple-600',
  yellow: 'from-yellow-500 to-yellow-600',
  teal: 'from-teal-500 to-teal-600',
  lime: 'from-lime-500 to-lime-600',
  fuchsia: 'from-fuchsia-500 to-fuchsia-600',
  green: 'from-green-500 to-green-600',
  emerald: 'from-emerald-500 to-emerald-600',
  blue: 'from-blue-500 to-blue-600',
};

// Стили для бейджей этапов
export const STAGE_BADGE_STYLES = {
  amber: 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400',
  orange: 'bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400',
  cyan: 'bg-cyan-100 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-400',
  rose: 'bg-rose-100 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400',
  violet: 'bg-violet-100 dark:bg-violet-900/20 text-violet-700 dark:text-violet-400',
  indigo: 'bg-indigo-100 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400',
  purple: 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400',
  yellow: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400',
  teal: 'bg-teal-100 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400',
  lime: 'bg-lime-100 dark:bg-lime-900/20 text-lime-700 dark:text-lime-400',
  fuchsia: 'bg-fuchsia-100 dark:bg-fuchsia-900/20 text-fuchsia-700 dark:text-fuchsia-400',
  green: 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400',
  emerald: 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400',
  blue: 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400',
};

// Получить иконку этапа по имени
export const getStageIcon = (stageName) => {
  return STAGE_ICONS[stageName] || Package;
};

// Получить конфиг этапа по имени
export const getStageConfig = (stageName) => {
  return STAGES_CONFIG[stageName] || { label: stageName, icon: Package, color: 'gray' };
};

export const getStagesFromRoles = (rolesValue) => {
  let roles = [];
  try {
    roles = typeof rolesValue === 'string' ? JSON.parse(rolesValue || '[]') : (rolesValue || []);
  } catch {
    roles = [];
  }

  if (!Array.isArray(roles)) return [];
  if (roles.includes('all')) return USER_STAGE_ORDER;

  const stageIds = new Set();
  roles.forEach((role) => {
    switch (role) {
      case 'Доставка от клиента':
      case 'pickup':
        stageIds.add('pickup');
        break;
      case 'Очистка':
      case 'Стирка':
      case 'washing':
        stageIds.add('washing_laundry');
        stageIds.add('washing_cleaning');
        break;
      case 'Прачечная':
      case 'Прачка':
      case 'washing_laundry':
        stageIds.add('washing_laundry');
        break;
      case 'Чистка':
      case 'washing_cleaning':
        stageIds.add('washing_cleaning');
        break;
      case 'Химчистка':
      case 'Химчистка мебели':
      case 'cleaning':
        stageIds.add('cleaning');
        break;
      case 'Сушка и упаковка':
      case 'drying_packing':
        stageIds.add('drying_packing');
        break;
      case 'Согласование':
      case 'approval':
        stageIds.add('approval');
        break;
      case 'Доставка клиенту':
      case 'delivery':
        stageIds.add('delivery');
        break;
      case 'Выдача':
      case 'handover':
        stageIds.add('handover');
        break;
      default:
        break;
    }
  });

  return USER_STAGE_ORDER.filter((stageId) => stageIds.has(stageId));
};
